/**
 * **이벤트 큐** — 봉투를 모아 `POST /api/analytics/collect` 로 묶어 보낸다.
 *
 * - 5초 또는 20건마다 보낸다. 언로드(`pagehide`)에는 `sendBeacon` 으로 남은 것을 흘려보낸다.
 * - 실패분은 localStorage 에 보류했다가 다음 flush 에 앞에 붙인다(최대 `RETRY_MAX` 건).
 * - 큐가 `QUEUE_MAX` 를 넘으면 오래된 것부터 버리고, 버린 수를 `events_dropped(queue_overflow)` 로 남긴다.
 * - 전송 함수는 주입한다(테스트 · `sendBeacon` 갈래). 토큰은 `bearerAuthHeader` 가 있으면 붙이고 없으면 게스트다.
 */

import type { AnalyticsClientEnvelope } from 'src/types/analytics/events';

import { bearerAuthHeader } from 'src/lib/firebase/auth-kit';

import { APP_VERSION_HEADER } from './version-skew';

export const COLLECT_PATH = '/api/analytics/collect';
export const BATCH_SIZE = 20;
export const FLUSH_INTERVAL_MS = 5_000;
export const QUEUE_MAX = 200;
export const RETRY_MAX = 100;

const KEY_RETRY = 'cm_analytics_retry';

export type SendFn = (batch: AnalyticsClientEnvelope[]) => Promise<boolean>;

export type AnalyticsQueueOptions = {
  send?: SendFn;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  /** 버린 이벤트를 세는 콜백 — client 가 `events_dropped` 를 만든다 */
  onDropped?: (reason: 'queue_overflow' | 'network', count: number) => void;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  /**
   * 응답 헤더가 알려 준 서버 판 버전 — 배포 판 어긋남 감지로 넘어간다(헤더가 없으면 null).
   * `sendBeacon` 갈래는 응답을 못 받으므로 여기로 오지 않는다.
   */
  onServerVersion?: (version: string | null) => void;
};

/**
 * 기본 전송 — fetch · 실패(네트워크·5xx·429)는 false. 4xx 는 다시 보내도 같으므로 true(버린다).
 * 응답이 오면 상태와 무관하게 서버 판 버전을 먼저 넘긴다 — 401 응답에도 헤더는 실려 있다.
 */
export function createFetchSender(onServerVersion?: (version: string | null) => void): SendFn {
  return async (batch) => {
    try {
      const res = await fetch(COLLECT_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await bearerAuthHeader()) },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      });
      onServerVersion?.(res.headers.get(APP_VERSION_HEADER));
      if (res.ok) return true;
      return res.status < 500 && res.status !== 429;
    } catch {
      return false;
    }
  };
}

/** 주입 없이 쓰는 기본 전송 */
export const sendWithFetch: SendFn = createFetchSender();

export class AnalyticsQueue {
  private items: AnalyticsClientEnvelope[] = [];

  private timer: unknown = null;

  private flushing = false;

  private readonly send: SendFn;

  private readonly storage: AnalyticsQueueOptions['storage'];

  private readonly onDropped: AnalyticsQueueOptions['onDropped'];

  private readonly setTimer: NonNullable<AnalyticsQueueOptions['setTimer']>;

  private readonly clearTimer: NonNullable<AnalyticsQueueOptions['clearTimer']>;

  constructor(options: AnalyticsQueueOptions = {}) {
    this.send = options.send ?? createFetchSender(options.onServerVersion);
    this.storage = options.storage === undefined ? safeStorage() : options.storage;
    this.onDropped = options.onDropped;
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = options.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  get size(): number {
    return this.items.length;
  }

  enqueue(envelope: AnalyticsClientEnvelope): void {
    this.items.push(envelope);
    if (this.items.length > QUEUE_MAX) {
      const overflow = this.items.length - QUEUE_MAX;
      this.items.splice(0, overflow);
      this.onDropped?.('queue_overflow', overflow);
    }
    if (this.items.length >= BATCH_SIZE) {
      void this.flush();
      return;
    }
    if (this.timer === null) {
      this.timer = this.setTimer(() => {
        this.timer = null;
        void this.flush();
      }, FLUSH_INTERVAL_MS);
    }
  }

  /** 보류분 + 큐를 한 번에 보낸다. 실패하면 보류에 남긴다 */
  async flush(): Promise<void> {
    if (this.flushing) return;
    if (this.timer !== null) {
      this.clearTimer(this.timer);
      this.timer = null;
    }
    const batch = [...this.readRetry(), ...this.items];
    this.items = [];
    if (batch.length === 0) return;

    this.flushing = true;
    try {
      for (let i = 0; i < batch.length; i += BATCH_SIZE) {
        const chunk = batch.slice(i, i + BATCH_SIZE);
        const ok = await this.send(chunk);
        if (!ok) {
          this.writeRetry([...chunk, ...batch.slice(i + BATCH_SIZE)]);
          return;
        }
      }
      this.writeRetry([]);
    } finally {
      this.flushing = false;
    }
  }

  /**
   * 언로드 직전 — 비동기를 기다릴 수 없으니 `sendBeacon` 으로 흘려보낸다.
   * 토큰을 붙일 수 없어 **게스트로 도착한다**(서버는 `anonymous_id` 로 잇는다). 없으면 보류에 남긴다.
   */
  flushWithBeacon(beacon: ((url: string, data: Blob) => boolean) | null): void {
    const batch = [...this.readRetry(), ...this.items];
    this.items = [];
    if (batch.length === 0) return;
    const body = new Blob([JSON.stringify({ events: batch })], { type: 'application/json' });
    const sent = beacon ? beacon(COLLECT_PATH, body) : false;
    this.writeRetry(sent ? [] : batch);
  }

  private readRetry(): AnalyticsClientEnvelope[] {
    if (!this.storage) return [];
    try {
      const raw = this.storage.getItem(KEY_RETRY);
      const parsed = raw ? (JSON.parse(raw) as unknown) : [];
      return Array.isArray(parsed) ? (parsed as AnalyticsClientEnvelope[]) : [];
    } catch {
      return [];
    }
  }

  private writeRetry(batch: AnalyticsClientEnvelope[]): void {
    if (batch.length > RETRY_MAX) {
      this.onDropped?.('network', batch.length - RETRY_MAX);
      batch = batch.slice(batch.length - RETRY_MAX);
    }
    if (!this.storage) return;
    try {
      if (batch.length === 0) this.storage.removeItem(KEY_RETRY);
      else this.storage.setItem(KEY_RETRY, JSON.stringify(batch));
    } catch {
      // 저장소가 막혀 있으면 보류 없이 버린다 — 로그가 화면을 막으면 안 된다
    }
  }
}

function safeStorage(): AnalyticsQueueOptions['storage'] {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
